import React, { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/**
 * DatePicker with es-AR locale (dd/MM/yyyy).
 *
 * Props:
 *  - value: ISO date string "YYYY-MM-DD" or ""
 *  - onChange: (isoString | "") => void
 *  - placeholder
 *  - testid
 *  - allowClear
 *  - className: extra trigger classes
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = "dd/mm/aaaa",
  testid,
  allowClear = true,
  className = "",
  size = "md",
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const display = selected && isValid(selected) ? format(selected, "dd/MM/yyyy", { locale: es }) : "";

  const heightClass = size === "sm" ? "h-9" : "h-10";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testid}
          className={`w-full justify-start text-left font-normal bg-white border-[#E5E0D8] hover:bg-[#FBF8F2] ${heightClass} ${
            display ? "text-[#1A1A1A]" : "text-[#8C8C8C]"
          } ${className}`}
        >
          <CalIcon className="mr-2 h-4 w-4 text-[#8C8C8C] shrink-0" />
          <span className="flex-1 truncate">{display || placeholder}</span>
          {allowClear && display && (
            <span
              role="button"
              aria-label="Limpiar fecha"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              className="ml-2 text-[#8C8C8C] hover:text-[#C85A32] text-lg leading-none"
            >
              ×
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 max-w-[calc(100vw-1rem)] max-h-[var(--radix-popover-content-available-height)] overflow-auto p-0" collisionPadding={8} align="start">
        <Calendar
          mode="single"
          defaultMonth={display ? selected : undefined}
          selected={display ? selected : undefined}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          locale={es}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
